import { useMutation, useQueryClient } from '@tanstack/react-query';
import { assignConversation, isApiError } from '../../api';
import { invalidateConversationLists, syncConversation } from './cache';
import { conversationKey } from './keys';

/** Mensagem para a pessoa quando atribuir falha (o texto cru do servidor nem sempre serve). */
export function assignErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    // O cliente de API já troca o texto cru por uma frase em português (outra aba mudou a sessão).
    if (error.code === 'invalid_csrf') return error.message;
    if (error.code === 'already_assigned') return 'Outra pessoa já assumiu esta conversa.';
    if (error.code === 'user_inactive') return 'Esse usuário está desativado.';
    if (error.code === 'invalid_user') return 'Usuário não encontrado.';
    if (error.status === 403) return 'Você não pode fazer essa alteração nesta conversa.';
    if (error.status >= 500)
      return 'O servidor está indisponível no momento. Tente de novo em instantes.';
    return error.message;
  }
  return 'Não foi possível atribuir a conversa. Tente de novo.';
}

/**
 * Assumir, liberar ou transferir uma conversa. `assign()` sem argumento assume para si; `null`
 * libera; um id transfere (só administrador). Ao terminar, a conversa aberta e as listas mudam:
 * a conversa pode entrar ou sair de "Minhas" e "Não atribuídas". Se outra pessoa assumiu antes
 * (409), a conversa é buscada de novo para a tela mostrar quem ficou com ela.
 */
export function useAssignConversation(conversationId: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (userId?: string | null) => assignConversation(conversationId, userId),
    onSuccess: (conversation) => {
      syncConversation(queryClient, conversation);
      void invalidateConversationLists(queryClient);
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
      void invalidateConversationLists(queryClient);
    },
  });
  return {
    assign: (userId?: string | null) => mutation.mutate(userId),
    pending: mutation.isPending,
    error: mutation.isError ? assignErrorMessage(mutation.error) : null,
    reset: mutation.reset,
  };
}
